/* eslint-disable @typescript-eslint/no-unused-vars */
import type { Maybe } from '@backmarket/utils/types/Maybe'
import { z } from 'zod'

import { createBlockSchema } from '../schemas/create-block-schema'
import type { LoosenRichTextFields } from '../types/loose-rich-text'
import type { Equal, ExpectTrue } from '../types/validation'

import { type ApiBackground, ApiBackgroundSchema } from './background'
import type { _ApiBlock } from './block'
import { ButtonsApiPropsSchema } from './buttons'
import { CmsButtonApiPropsSchema } from './cms-button'
import { type ContentLink, ContentLinkSchema } from './content-link'
import { type CTALink, CTALinkSchema } from './cta-link'
import { type Image, ImageSchema } from './image'
import { ListItemSchema } from './list-item'
import { MediaApiPropsSchema } from './media'
import { RichTextApiPropsSchema } from './rich-text-content'
/* TypeScript */
type ModularCardSize = 'small' | 'medium'
type ModularCardLayout = 'grid' | 'carousel'

export type ModularCardsCollectionApiProps = {
  technicalName?: string
  title?: string
  subtitle?: string
  size: ModularCardSize
  cards: ModularCard[]
  cardsHavePadding?: boolean
  // "layout" is for the mobile layout only, the name cannot be changed due to retro compatibility reasons.
  layout?: ModularCardLayout
  desktopLayout?: ModularCardLayout
  mobileScreenColumns?: 1 | 2
  ctaLink?: CTALink
}

export type ModularCard = {
  technicalName?: string
  id: string
  sections: ModularCardSection[]
  size?: ModularCardSize
  background?: Maybe<ApiBackground>
  link?: ContentLink
}

export type ModularCardSection = {
  alignment?: ModularCardSectionAlignment
  slots: Array<
    | _ApiBlock<'Button'>
    | _ApiBlock<'Buttons'>
    | _ApiBlock<'Media'>
    | _ApiBlock<'ModularCardImage'>
    | _ApiBlock<'ListItem'>
    | _ApiBlock<'RichText'>
  >
}

export type ModularCardImageApiProps = {
  technicalName?: string
  image: Image
  link?: ContentLink
  rounded?: boolean
}

export type ModularCardSectionAlignment = 'center' | 'left'

/* Schemas */
export const ModularCardSizeSchema = z.enum(['small', 'medium'])
export const ModularCardSectionAlignmentSchema = z.enum(['center', 'left'])
export const ModularCardLayoutSchema = z.enum(['grid', 'carousel'])

export const ModularCardImageApiPropsSchema = z.object({
  technicalName: z.string().optional(),
  image: ImageSchema,
  link: ContentLinkSchema.optional(),
  rounded: z.boolean().optional(),
})

export const ModularCardSectionSchema = z.object({
  alignment: ModularCardSectionAlignmentSchema.optional(),
  slots: z
    .union([
      createBlockSchema('Button', CmsButtonApiPropsSchema),
      createBlockSchema('Buttons', ButtonsApiPropsSchema),
      createBlockSchema('Media', MediaApiPropsSchema),
      createBlockSchema('ModularCardImage', ModularCardImageApiPropsSchema),
      createBlockSchema('ListItem', ListItemSchema),
      createBlockSchema('RichText', RichTextApiPropsSchema),
    ])
    .array(),
})

export const ModularCardSchema = z.object({
  technicalName: z.string().optional(),
  id: z.string(),
  sections: z.array(ModularCardSectionSchema),
  size: ModularCardSizeSchema.optional(),
  background: ApiBackgroundSchema.nullable().optional(),
  link: ContentLinkSchema.optional(),
})

export const ModularCardsCollectionApiPropsSchema = z.object({
  technicalName: z.string().optional(),
  title: z.string().optional(),
  subtitle: z.string().optional(),
  size: ModularCardSizeSchema,
  cards: z.array(ModularCardSchema),
  cardsHavePadding: z.boolean().optional(),
  layout: ModularCardLayoutSchema.optional(),
  desktopLayout: ModularCardLayoutSchema.optional(),
  mobileScreenColumns: z.union([z.literal(1), z.literal(2)]).optional(),
  ctaLink: CTALinkSchema.optional(),
})

/* Schemas validation */
type TestModularCardsCarouselProps = ExpectTrue<
  Equal<
    LoosenRichTextFields<ModularCardsCollectionApiProps>,
    z.infer<typeof ModularCardsCollectionApiPropsSchema>
  >
>
